import { wktToGeoJSON } from "@terraformer/wkt";
import { FeatureCollection, MultiPolygon } from "geojson";
import { GeoData } from "@rogoag/shared-ts";
import { AgrianTypes, ProagricaTypes } from "../models";

function _getBoundaryGeoJSON<T>(records: T[], getWKT: (record: T) => string, getArea: (record: T) => number) {
	return {
		type: "FeatureCollection",
		features: records.map((record) => {
			const geoJSON = wktToGeoJSON(getWKT(record));
			return {
				geometry: geoJSON,
				type: "Feature",
				properties: {
					measure: getArea(record),
				},
			};
		}),
	} as GeoJSON.FeatureCollection;
}

export namespace Proagrica {
	export function getBoundaryGeoJSON(records: ProagricaTypes.BoundaryRecord[]) {
		return _getBoundaryGeoJSON(
			records,
			(record) => record.WKT,
			(record) => record.Measure.Value,
		);
	}

	export async function getZoneOrPointsGeoJSON(
		fieldSamplingItem: ProagricaTypes.SamplingActivity,
	): Promise<FeatureCollection> {
		const geoJSONObjects = await Promise.all(
			fieldSamplingItem.SoilSamplingRecords.map(async (record) => {
				const geoJSON = await GeoData.toGeoJSON(record.WKT, "WKT");
				if (geoJSON.features.length > 0) {
					geoJSON.features[0].properties = {
						SampleID: record.SoilSamplingData?.SampleID?.Value ?? 0,
						ApplicationDate: fieldSamplingItem.ApplicationDate,
						Depth: record.SoilSamplingData?.TopsoilSamplingDepth?.Depth?.Value ?? 0,
						Units: (record.SoilSamplingData?.TopsoilSamplingDepth?.UnitID?.Value ?? 0) === 52 ? "in" : "",
					};
				}
				return geoJSON;
			}),
		);

		return {
			type: "FeatureCollection",
			features: geoJSONObjects.flatMap((geojson) => geojson.features),
		};
	}
}

export namespace Agrian {
	export function getBoundaryGeoJSON(records: AgrianTypes.BoundaryMap[]) {
		records = records.filter((r) => !!r);
		return _getBoundaryGeoJSON(
			records,
			(record) => record.boundary,
			(record) => record.area ?? 0,
		);
	}

	export async function getSampleEventCentroidsGeoJSON(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): Promise<FeatureCollection | undefined> {
		const geoJson = await getSampleEventSamples(sampleEvent);
		geoJson.features = GeoData.separatePointsFromPolygons([geoJson]).points.features;
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export async function getSampleEventCoresGeoJSON(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): Promise<FeatureCollection | undefined> {
		const geoJson = await getSampleEventSubSamples(sampleEvent);
		geoJson.features = GeoData.separatePointsFromPolygons([geoJson]).points.features;
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export async function getSampleEventZonesGeoJSON(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): Promise<FeatureCollection | undefined> {
		const sampleGeoJson = await getSampleEventSamples(sampleEvent);
		sampleGeoJson.features = GeoData.separatePointsFromPolygons([sampleGeoJson]).polygons.features;

		const subSampleGeoJson = await getSampleEventSubSamples(sampleEvent);
		subSampleGeoJson.features = GeoData.separatePointsFromPolygons([subSampleGeoJson]).polygons.features;

		const geoJson = GeoData.merge([sampleGeoJson, subSampleGeoJson]);
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export async function getSampleEventSamples(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): Promise<FeatureCollection> {
		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const sample of sampleEvent.samples) {
			index++;
			const geoJsonGeometry = await GeoData.toGeoJSON(sample.geometry, "WKT");
			if (geoJsonGeometry.features.length > 0) {
				const n = Number(sample.sample_number);
				const sampleNumber = !isNaN(n) ? n : index;
				geoJsonGeometry.features.forEach((feature) => {
					if (!feature.properties) feature.properties = {};
					if (feature.geometry.type === "Point") {
						feature.properties.PointID = sampleNumber;
						if (sample.id) feature.properties.SampleGuid = sample.id;
					} else {
						feature.properties.ZoneID = sampleNumber;
						if (sample.id) feature.properties.ZoneGuid = sample.id;
					}
				});
			}
			geoJsons.push(geoJsonGeometry);
		}

		const geoJson = GeoData.merge(geoJsons);
		return geoJson;
	}

	export async function getSampleEventSubSamples(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): Promise<FeatureCollection> {
		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const sample of sampleEvent.samples) {
			index++;
			let subIndex = 0;
			for (const subSample of sample.sub_samples) {
				subIndex++;
				const geoJsonGeometry = await GeoData.toGeoJSON(subSample.geometry, "WKT");
				if (geoJsonGeometry.features.length > 0) {
					const n = Number(subSample.sub_sample_number);
					const subSampleNumber = !isNaN(n) ? n : `${index}.${subIndex}`;
					geoJsonGeometry.features.forEach((feature) => {
						if (!feature.properties) feature.properties = {};
						if (feature.geometry.type === "Point") {
							feature.properties.PointID = subSampleNumber;
							if (subSample.id) feature.properties.SubSampleGuid = subSample.id;
						} else {
							feature.properties.ZoneID = subSampleNumber;
							if (subSample.id) feature.properties.SubSampleGuid = subSample.id;
						}
					});
				}
				geoJsons.push(geoJsonGeometry);
			}
		}

		const geoJson = GeoData.merge(geoJsons);
		return geoJson;
	}

	export async function getTargetEventCentroidsGeoJSON(
		targetEvent: AgrianTypes.HydratedTargetEvent,
	): Promise<FeatureCollection | undefined> {
		const geoJson = await getTargetEventSamples(targetEvent);
		geoJson.features = GeoData.separatePointsFromPolygons([geoJson]).points.features;

		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export async function getTargetEventZonesGeoJSON(
		targetEvent: AgrianTypes.HydratedTargetEvent,
	): Promise<FeatureCollection | undefined> {
		const geoJson = await getTargetEventSamples(targetEvent);
		geoJson.features = GeoData.separatePointsFromPolygons([geoJson]).polygons.features;

		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export async function getTargetEventSamples(event: AgrianTypes.HydratedTargetEvent): Promise<FeatureCollection> {
		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const sample of event.target_samples) {
			index++;
			const geoJsonGeometry = await GeoData.toGeoJSON(sample.geometry, "WKT");
			if (geoJsonGeometry.features.length > 0) {
				const n = Number(sample.sample_number);
				const sampleNumber = !isNaN(n) ? n : index;
				geoJsonGeometry.features.forEach((feature) => {
					if (!feature.properties) feature.properties = {};
					if (feature.geometry.type === "Point") {
						feature.properties.SampleID = sampleNumber;
						if (sample.id) feature.properties.SampleGuid = sample.id;
					} else {
						feature.properties.ZoneID = sampleNumber;
						if (sample.id) feature.properties.ZoneGuid = sample.id;
					}
				});
			}
			geoJsons.push(geoJsonGeometry);
		}

		const geoJson = GeoData.merge(geoJsons);
		return geoJson;
	}

	export async function getZoneMapGeoJSON(zones: AgrianTypes.HydratedZone): Promise<FeatureCollection | undefined> {
		function convertMultiPolygonToWKT(multipolygon: MultiPolygon): string {
			if (multipolygon.type !== "MultiPolygon") {
				throw new Error("Invalid GeoJSON type. Expected 'MultiPolygon'.");
			}

			const wkt = multipolygon.coordinates
				.map(
					(polygon) => `(${polygon.map((ring) => `(${ring.map((coord) => coord.join(" ")).join(", ")})`).join(", ")})`,
				)
				.join(", ");

			return `MULTIPOLYGON(${wkt})`;
		}

		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const zone of zones.zones) {
			index++;
			const wkt = convertMultiPolygonToWKT(zone.geometry);
			const geoJSON = await GeoData.toGeoJSON(wkt, "WKT");
			if (geoJSON.features.length > 0) {
				const n = Number(zone.id);
				const zoneNumber = !isNaN(n) ? n : index;
				geoJSON.features.forEach((feature) => {
					if (!feature.properties) feature.properties = {};
					feature.properties.ZoneID = zoneNumber;
					if (zone.id) feature.properties.ZoneGuid = zone.id;
				});
			}
			geoJsons.push(geoJSON);
		}

		const geoJson = GeoData.merge(geoJsons);
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}
}
